import { useMemo, useState } from 'react';
import {
  ActionableNotification,
  Button,
  InlineNotification,
  RadioTile,
  Search,
  SkeletonText,
  Slider,
  Tag,
  TileGroup,
} from '@carbon/react';
import { CheckmarkFilled, WarningFilled } from '@carbon/react/icons';
import classNames from 'classnames';
import plur from 'plur';
import { format0 } from 'utils/number';
import {
  CaseIncompleteNote,
  DomainMissingLine,
  NothingToValueRow,
  PendingCoverageList,
} from './case-coverage-v2';
import { DrillInHeader } from './drill-in-header';
import { CoverageListError, useCaseCoverageList } from './hooks';
import { coverageBand, isFullyCovered, partitionCoverage } from './utils';
import { coverageReadout } from './coverage-utils';
import type { CasePickerState, EvaluationCaseCoverage } from './types';
import styles from './case-picker-drill-in.module.scss';

/** Display label per case `evaluationType` (raw enum values otherwise). */
const EVALUATION_TYPE_LABELS: Record<string, string> = {
  acquisition: 'Acquisition',
  divestiture: 'Divestiture',
  mtm: 'MTM',
};

export interface CasePickerDrillInProps {
  /** The package the coverage list is scoped to. */
  packageId: number;
  /** The working config's active case — the "Current" tag + preselection. */
  activeCaseId: number | null;
  /** The lifted Min-coverage slider state (survives preview round-trips, FR#14). */
  pickerState: CasePickerState;
  /** Writes the lifted slider state back to the section. */
  onPickerStateChange: (state: CasePickerState) => void;
  /** Commit a case (a tile pick) — the section stores it and returns to main. */
  onSelect: (caseRow: EvaluationCaseCoverage) => void;
  /** Open the Uncovered drill-in for a case WITHOUT committing it (preview). */
  onPreviewUncovered: (caseRow: EvaluationCaseCoverage) => void;
  /** Return to the main panel view. */
  onBack: () => void;
}

/**
 * The Case Picker drill-in (P4 FR#3): a persistent partial-coverage info banner
 * and a Min-coverage Slider (0–100, step 5 — bound to the LIFTED `pickerState`,
 * so the value survives preview round-trips, FR#14) over `coverage_list`-sourced
 * RadioTiles — each showing the case name (+ a blue "Current" tag on the working
 * config's active case), its `evaluationType`, a health-banded coverage bar
 * (FR#4), "[X]% covers this package", and either an all-covered row or the
 * "Missing N assets, N units, N slots" breakdown. The slider re-partitions the
 * ONE `threshold=0` fetch client-side (`partitionCoverage` — no refetch); cases
 * below it collapse behind a "Show N cases below threshold" reveal, and moving
 * the slider re-collapses them (`showAll` resets). Picking a tile commits the
 * case (`onSelect`); the breakdown row / View Missing preview a case's gaps
 * WITHOUT committing it — every interaction on them stops propagation (click
 * AND Enter/Space) so a preview can never select the surrounding tile.
 *
 * @param {CasePickerDrillInProps} props - The package id, lifted state, and the pick/preview/back handlers.
 * @return {JSX.Element} The case picker drill-in surface.
 */
export function CasePickerDrillIn({
  packageId,
  activeCaseId,
  pickerState,
  onPickerStateChange,
  onSelect,
  onPreviewUncovered,
  onBack,
}: CasePickerDrillInProps) {
  const { data, isPending, isError, error, refetch } =
    useCaseCoverageList(packageId);

  // A non-retryable failure (BE `retryable: false`, e.g. a deterministic
  // query_timeout) can never succeed on retry — offering the button would
  // only invite users to hammer an endpoint that already said no.
  const retryable = !(error instanceof CoverageListError && !error.retryable);

  // The full coverage-desc list — the BE splits by the REQUESTED threshold (0
  // here), so everything arrives in `aboveThreshold`; the concat keeps this
  // correct even if a cached entry was fetched with another threshold.
  const cases = useMemo<EvaluationCaseCoverage[]>(
    () => (data ? [...data.aboveThreshold, ...data.belowThreshold] : []),
    [data]
  );

  // The name search: case-insensitive, over the loaded list (the counted cases and the ones not
  // counted yet), client-side. Local to the drill-in, so it clears when the drill-in closes.
  const [query, setQuery] = useState('');
  const term = query.trim().toLowerCase();
  const matchingCases = useMemo(() => {
    if (!term) return cases;
    return cases.filter((row) => row.name.toLowerCase().includes(term));
  }, [cases, term]);

  // Client-side split at the slider threshold (order preserved — the BE list is
  // coverage-desc). Below-threshold cases stay hidden until revealed.
  const { above, below } = useMemo(
    () => partitionCoverage(matchingCases, pickerState.threshold),
    [matchingCases, pickerState.threshold]
  );
  const visibleCases = pickerState.showAll ? matchingCases : above;

  // Case-level coverage comes from a cache, so some cases may not be counted yet (calculating, or
  // unavailable); and a case may be incomplete. A failed poll keeps the last 'calculating' body as
  // `data`, and polling stops, so its rows would stay up for good: once the query is in error, the
  // error card says what happened.
  const calculating = !isError ? (data?.calculating ?? []) : [];
  const unavailable = !isError ? (data?.unavailable ?? []) : [];
  const pendingCount = calculating.length + unavailable.length;
  const matchingCalculating = term
    ? calculating.filter((row) => row.name.toLowerCase().includes(term))
    : calculating;
  const matchingUnavailable = term
    ? unavailable.filter((row) => row.name.toLowerCase().includes(term))
    : unavailable;
  const anyCases = cases.length > 0 || pendingCount > 0;
  const anyMatch =
    matchingCases.length > 0 ||
    matchingCalculating.length > 0 ||
    matchingUnavailable.length > 0;

  /**
   * A breakdown-row activation: preview the case's uncovered assets WITHOUT
   * selecting the tile — the RadioTile wraps this row, so the event must not
   * reach it (FR#3 interaction hygiene).
   *
   * @param {Event} event - The click / keyboard event to neutralize.
   * @param {EvaluationCaseCoverage} caseRow - The case to preview.
   * @return {void}
   */
  const previewFromRow = (
    event: { preventDefault: () => void; stopPropagation: () => void },
    caseRow: EvaluationCaseCoverage
  ): void => {
    event.preventDefault();
    event.stopPropagation();
    onPreviewUncovered(caseRow);
  };

  return (
    <div className={styles.picker}>
      <DrillInHeader
        title="Select Evaluation Case"
        subtitle="Showing cases that overlap this package"
        onBack={onBack}
      />

      {/* Always shown, never dismissible — the consequence applies to every pick. */}
      <InlineNotification
        className={styles.banner}
        kind="info"
        lowContrast
        hideCloseButton
        title="Partial coverage excludes uncovered assets."
        subtitle="If you pick a case below 100% coverage, the assets, units, and slots outside the case won't be valued in this evaluation."
      />

      {isPending && <SkeletonText paragraph lineCount={6} />}

      {isError && retryable && (
        <ActionableNotification
          className={styles.banner}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump)
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the evaluation cases"
          subtitle={
            // The envelope's own message (a 503's "try again in a few minutes").
            error instanceof Error
              ? error.message
              : 'The coverage list is unavailable right now.'
          }
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetch()}
        />
      )}

      {isError && !retryable && (
        <InlineNotification
          className={styles.banner}
          kind="error"
          // role="alert" (not InlineNotification's default "status"): the
          // branch the user can do nothing about must not be announced more
          // quietly than the retryable one above.
          role="alert"
          lowContrast
          hideCloseButton
          title="Couldn't load the evaluation cases"
          subtitle={
            error instanceof CoverageListError && error.code === 'query_timeout'
              ? "Coverage took too long to compute for this package. Retrying won't help — the team has been notified."
              : (error?.message ??
                'The coverage list is unavailable right now.')
          }
        />
      )}

      {/* Shown whenever there is any case, counted or not yet; it filters both lists. */}
      {anyCases && (
        <Search
          id="eval3-case-picker-search"
          size="sm"
          labelText="Search cases"
          placeholder="Search cases"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      )}

      <PendingCoverageList
        calculating={matchingCalculating}
        unavailable={matchingUnavailable}
      />

      {!isPending && !isError && !anyCases && (
        <p className={styles.emptyText}>
          No evaluation cases overlap this package.
        </p>
      )}

      {anyCases && !anyMatch && (
        <p className={styles.emptyText}>No cases match</p>
      )}

      {/* Min-coverage slider (FR#3) — bound to the LIFTED state; moving it
          re-partitions locally (no refetch) and re-collapses the reveal. */}
      {cases.length > 0 && (
        <Slider
          className={styles.slider}
          id="eval3-case-picker-threshold"
          labelText="Min coverage"
          min={0}
          max={100}
          step={5}
          value={pickerState.threshold}
          hideTextInput
          formatLabel={(value) => `${format0(value)}%`}
          onChange={({ value }) =>
            onPickerStateChange({ threshold: value, showAll: false })
          }
        />
      )}

      {/* Every case sits below the threshold and the reveal is collapsed — the
          list would otherwise render nothing, so say why. (`cases` non-empty
          already implies data resolved, so no loading/error guard — matching
          the slider + tiles in the stale-data-plus-error state.) */}
      {matchingCases.length > 0 && visibleCases.length === 0 && (
        <p className={styles.emptyText}>No cases meet the minimum coverage.</p>
      )}

      {visibleCases.length > 0 && (
        <TileGroup
          className={styles.tileGroup}
          name="eval3-case-picker"
          legend="Available cases"
          // Preselect the working config's active case (Change-case entry,
          // FR#3). Stringified — TileGroup compares tile values strictly, so
          // both sides use String(id) (the case-editor picker's pattern).
          valueSelected={
            activeCaseId !== null ? String(activeCaseId) : undefined
          }
          onChange={(value: string | number) => {
            const picked = cases.find((row) => row.id === Number(value));
            if (picked) {
              onSelect(picked);
            }
          }}
        >
          {visibleCases.map((row) => {
            const allCovered = isFullyCovered(row);
            const readout = coverageReadout(row);
            const nothingToValue = row.nothingToValue === true;
            return (
              <RadioTile
                key={row.id}
                id={`eval3-case-tile-${row.id}`}
                className={styles.tile}
                // String — must match `valueSelected`'s String(id) strictly.
                value={String(row.id)}
              >
                <div className={styles.tileTitleRow}>
                  <span className={styles.tileName}>{row.name}</span>
                  {/* The working config's ACTIVE case (what the form would send)
                      — not the last-run case (P4 decision). */}
                  {row.id === activeCaseId && (
                    <Tag className={styles.currentTag} type="blue" size="sm">
                      Current
                    </Tag>
                  )}
                </div>
                <span className={styles.tileMeta}>
                  {EVALUATION_TYPE_LABELS[row.evaluationType] ??
                    row.evaluationType}
                </span>
                <CaseIncompleteNote row={row} />

                <div className={styles.coverageRow}>
                  <div className={styles.coverageTrack}>
                    {typeof readout.fillPct === 'number' && (
                      <div
                        data-testid="coverage-fill"
                        className={classNames(
                          styles.coverageFill,
                          styles[coverageBand(readout.fillPct)]
                        )}
                        // Dynamic width — the one value CSS can't predetermine.
                        style={{ width: `${readout.fillPct}%` }}
                      />
                    )}
                  </div>
                  <span className={styles.coveragePct}>{readout.pctText}</span>
                  <span className={styles.coverageLabel}>{readout.label}</span>
                </div>

                {nothingToValue ? (
                  <NothingToValueRow text={row.nothingToValueText} />
                ) : allCovered ? (
                  <div className={styles.allCoveredRow}>
                    <CheckmarkFilled
                      aria-hidden="true"
                      className={styles.allCoveredIcon}
                    />
                    <span>All assets covered</span>
                  </div>
                ) : (
                  // The preview affordance nested INSIDE the selectable tile:
                  // stop propagation + prevent default on every interaction so a
                  // preview never commits the case (FR#3).
                  <div
                    className={styles.breakdownRow}
                    role="button"
                    tabIndex={0}
                    aria-label={`Preview uncovered assets for ${row.name}`}
                    onClick={(event) => previewFromRow(event, row)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        previewFromRow(event, row);
                      }
                    }}
                  >
                    <span className={styles.breakdownText}>
                      <WarningFilled
                        aria-hidden="true"
                        className={styles.breakdownIcon}
                      />
                      Missing <strong>{format0(row.missing.assets)}</strong>{' '}
                      assets, <strong>{format0(row.missing.units)}</strong>{' '}
                      units, <strong>{format0(row.missing.slots)}</strong> slots
                    </span>
                    {/* Visual affordance only — the ROW is the single activator
                        (its aria-label names the action), so this carries no
                        handler and stays out of the a11y tree: a second nested
                        interactive element would be invalid inside the row. */}
                    <span className={styles.breakdownLink} aria-hidden="true">
                      View Missing
                    </span>
                  </div>
                )}
                {!allCovered && !nothingToValue && (
                  <DomainMissingLine domainMissing={row.domainMissing} />
                )}
              </RadioTile>
            );
          })}
        </TileGroup>
      )}

      {/* Reveal the collapsed below-threshold cases. A ghost Button (not an
          href-less Link — a dead anchor isn't keyboard-operable); once revealed
          it disappears (moving the slider re-collapses via the showAll reset).
          `below` non-empty implies data resolved — no loading/error guard. */}
      {below.length > 0 && !pickerState.showAll && (
        <Button
          className={styles.showBelowButton}
          kind="ghost"
          size="sm"
          onClick={() => onPickerStateChange({ ...pickerState, showAll: true })}
        >
          Show {format0(below.length)} {plur('case', below.length)} below
          threshold
        </Button>
      )}
    </div>
  );
}
